import { useLocalSearchParams } from 'expo-router';

import { useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

import { NormalAccountCreationScreen } from './components/NormalAccountCreationScreen';
import { PurlAccountCreationScreen } from './components/PurlAccountCreationScreen';
import { PurlRedirectScreen } from './components/PurlRedirectScreen';
import { SsoAccountCreationScreen } from './components/SsoAccountCreationScreen';

// Shell that picks the account-creation flow and renders the matching screen.
// The three flows are mutually exclusive:
//   - PURL  — reached once the token has validated AND the DOB has been confirmed
//             on the dedicated entry screen (purl.verifiedDob set). If a deep link
//             lands here with a token still in flight, we hold a loader while the
//             resolver routes on to /step/purl-dob-verification (the DOB entry /
//             already-enrolled steps live there), so the normal form never flashes.
//   - SSO   — a provisioned user carrying `customerProfile.ssoPayingOrganization`
//             (or the dev-only `?sso=` override).
//   - Normal — brand-new self-serve signup.
export function AccountCreationScreen() {
  const { user, purl } = useRulesEngine();
  // `?sso=<payingOrg>` is a dev-only override that flips into SSO mode without a
  // real SSO-provisioned user; ignored outside __DEV__.
  const { sso } = useLocalSearchParams<{ sso?: string }>();

  if (purl.status === 'valid' && purl.verifiedDob) {
    return <PurlAccountCreationScreen />;
  }
  // A PURL token is still being processed (a `?token=&org=` link landed on this
  // route while validating, or is valid but awaiting DOB confirmation). The
  // resolver is about to route to /step/purl-dob-verification — hold a loader
  // rather than flash the normal form. ('unset' is a normal user with no token;
  // 'locked' falls through to normal account creation.)
  if (purl.status === 'validating' || purl.status === 'valid') {
    return <LoadingScreen />;
  }
  // Terminal token result with a backend redirect (invalid / already-enrolled):
  // hand off to login (prod) or the sign-in sheet (dev). A network-error
  // 'invalid' has no redirectUrl, so it falls through to normal onboarding.
  if ((purl.status === 'invalid' || purl.status === 'already_enrolled') && purl.redirectUrl) {
    return <PurlRedirectScreen redirectUrl={purl.redirectUrl} />;
  }

  const isSso = !!user?.customerProfile?.ssoPayingOrganization || (__DEV__ && !!sso);
  if (isSso) {
    return <SsoAccountCreationScreen />;
  }

  return <NormalAccountCreationScreen />;
}
